import React, { useState, useCallback, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import {
  BLOCK_TYPES,
  DEFAULT_SETTINGS,
  generateId,
  SectionPreview,
  SectionProps,
} from './AdminHomePageEditor';
import RichTextEditor from '../components/RichTextEditor';
import './AdminHomePageEditor.css';

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

const emptyPageMeta = () => ({
  slug: '',
  titleLt: '',
  titleEn: '',
  titleDe: '',
  metaTitleLt: '',
  metaTitleEn: '',
  metaTitleDe: '',
  metaDescriptionLt: '',
  metaDescriptionEn: '',
  metaDescriptionDe: '',
  isActive: true,
  sortOrder: 0,
});

function AdminPageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const isNew = !id || id === 'new';

  const [sections, setSections] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [pageMeta, setPageMeta] = useState(emptyPageMeta());
  const [activeLang, setActiveLang] = useState('lt');

  const { data: page, isLoading } = useQuery({
    queryKey: ['admin-page-by-id', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/pages/by-id/${id}`);
      return res.data;
    },
    enabled: !isNew && !!id,
  });

  useEffect(() => {
    if (isNew) {
      setPageMeta(emptyPageMeta());
      setSections([]);
      return;
    }
    if (page) {
      setPageMeta({
        slug: page.slug || '',
        titleLt: page.title_lt || '',
        titleEn: page.title_en || '',
        titleDe: page.title_de || '',
        metaTitleLt: page.meta_title_lt || '',
        metaTitleEn: page.meta_title_en || '',
        metaTitleDe: page.meta_title_de || '',
        metaDescriptionLt: page.meta_description_lt || '',
        metaDescriptionEn: page.meta_description_en || '',
        metaDescriptionDe: page.meta_description_de || '',
        isActive: !!page.is_active,
        sortOrder: page.sort_order != null ? page.sort_order : 0,
      });
      const content = page.content;
      const list = content?.sections && Array.isArray(content.sections) ? content.sections : [];
      setSections(list);
    }
  }, [isNew, page]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      if (isNew) {
        const res = await apiClient.post('/admin/pages', payload);
        return res.data;
      }
      await apiClient.put(`/admin/pages/${id}`, payload);
      return { id: parseInt(id, 10) };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['admin-pages']);
      if (isNew && data.id) {
        notification.success('Puslapis sukurtas. Dabar galite redaguoti turinį.');
        navigate(`/admin/pages/${data.id}/edit`, { replace: true });
      } else {
        notification.success('Puslapis išsaugotas');
      }
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida išsaugant');
    },
  });

  const addBlock = useCallback((type) => {
    const settings = { ...DEFAULT_SETTINGS[type] };
    setSections((prev) => [...prev, { id: generateId(), type, settings }]);
    notification.success(`Blokas „${BLOCK_TYPES.find((b) => b.type === type)?.label}“ pridėtas`);
  }, [notification]);

  const updateSection = useCallback((sectionId, patch) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        const next = { ...s };
        if (patch.settings !== undefined) next.settings = { ...s.settings, ...patch.settings };
        if (patch.rowGroup !== undefined) next.rowGroup = patch.rowGroup || null;
        return next;
      })
    );
  }, []);

  const removeSection = useCallback((sectionId) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
    if (selectedId === sectionId) setSelectedId(null);
  }, [selectedId]);

  const moveSection = useCallback((fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= sections.length) return;
    setSections((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  }, [sections.length]);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    e.target.closest('.editor-section')?.classList.add('dragging');
  };
  const handleDragEnd = (e) => {
    e.target.closest('.editor-section')?.classList.remove('dragging');
    setDraggedIndex(null);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === dropIndex) return;
    moveSection(fromIndex, dropIndex);
  };

  const handleSave = () => {
    const payload = {
      ...pageMeta,
      contentLt: '',
      contentEn: '',
      contentDe: '',
      content: { sections },
    };
    if (isNew && (!payload.slug || !payload.titleLt)) {
      notification.error('Įveskite slug ir pavadinimą (LT)');
      return;
    }
    saveMutation.mutate(payload);
  };

  const previewUrl = pageMeta.slug && typeof window !== 'undefined'
    ? `${window.location.origin}/page/${pageMeta.slug}`
    : null;

  if (!isNew && isLoading) {
    return <div className="home-page-editor-loading">Kraunama...</div>;
  }

  return (
    <div className="home-page-editor">
      <div className="editor-header">
        <h1>{isNew ? 'Naujas puslapis' : `Redaguoti: ${pageMeta.titleLt || page?.title_lt || '(be pavadinimo)'}`}</h1>
        <div className="editor-lang-tabs">
          {LOCALES.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
              onClick={() => setActiveLang(code)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="editor-header-actions">
          <Link to="/admin/pages" className="btn btn-secondary">
            ← Grįžti į puslapių sąrašą
          </Link>
          {previewUrl && (
            <Link to={previewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              Atidaryti puslapį naujame lange
            </Link>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saveMutation.isLoading}
          >
            {saveMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>

      <div className="editor-body">
        <aside className="editor-panel editor-panel-left">
          <h3>Pridėti bloką</h3>
          <div className="block-types">
            {BLOCK_TYPES.map((block) => (
              <button
                key={block.type}
                type="button"
                className="block-type-btn"
                onClick={() => addBlock(block.type)}
              >
                <span className="block-type-icon">{block.icon}</span>
                <span>{block.label}</span>
              </button>
            ))}
          </div>
        </aside>

        <main className="editor-canvas">
          <p className="editor-canvas-hint">
            Vilkkite blokus norima tvarka. Spustelėkite bloką – jo nustatymai išsiskleis po bloku. Puslapio nustatymai – dešinėje.
          </p>
          {sections.length === 0 ? (
            <div className="editor-empty">
              <div className="editor-empty-current-structure">
                <h3>Turinio nėra</h3>
                <p>Pridėkite blokų iš kairės panelės. Spustelėkite bloką, kad redaguotumėte jo duomenis. Puslapio nustatymus (slug, pavadinimas) redaguokite dešinėje.</p>
              </div>
            </div>
          ) : (
            <div className="editor-sections">
              {sections.map((section, index) => (
                <div key={section.id} className="editor-section-wrap">
                  <div
                    className={`editor-section ${selectedId === section.id ? 'selected' : ''} ${draggedIndex === index ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    onClick={() => setSelectedId((prev) => (prev === section.id ? null : section.id))}
                  >
                    <div className="editor-section-handle" title="Vilkti">⋮⋮</div>
                    <div className="editor-section-preview">
                      <SectionPreview section={section} activeLang={activeLang} />
                    </div>
                    <span className="editor-section-toggle" title={selectedId === section.id ? 'Suskleisti nustatymus' : 'Išskleisti nustatymus'}>
                      {selectedId === section.id ? '▼' : '▶'}
                    </span>
                    <button
                      type="button"
                      className="editor-section-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSection(section.id);
                      }}
                      title="Pašalinti"
                    >
                      ×
                    </button>
                  </div>
                  {selectedId === section.id && (
                    <div className="editor-section-props-inline">
                      <h4>Savybės ({LOCALES.find((l) => l.code === activeLang)?.label})</h4>
                      <SectionProps
                        section={section}
                        sections={sections}
                        activeLang={activeLang}
                        onChange={(patch) => updateSection(section.id, patch)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>

        <aside className="editor-panel editor-panel-right">
          <h3>Puslapio nustatymai</h3>
          <PageSettingsForm pageMeta={pageMeta} setPageMeta={setPageMeta} activeLang={activeLang} locales={LOCALES} />
        </aside>
      </div>
    </div>
  );
}

function PageSettingsForm({ pageMeta, setPageMeta, activeLang, locales }) {
  const update = (key, value) => setPageMeta((prev) => ({ ...prev, [key]: value }));
  const titleKey = activeLang === 'lt' ? 'titleLt' : activeLang === 'en' ? 'titleEn' : 'titleDe';
  const metaTitleKey = activeLang === 'lt' ? 'metaTitleLt' : activeLang === 'en' ? 'metaTitleEn' : 'metaTitleDe';
  const metaDescKey = activeLang === 'lt' ? 'metaDescriptionLt' : activeLang === 'en' ? 'metaDescriptionEn' : 'metaDescriptionDe';
  const langLabel = locales?.find((l) => l.code === activeLang)?.label || activeLang;

  return (
    <div className="section-props">
      <label>Slug (URL dalis) *</label>
      <input
        type="text"
        value={pageMeta.slug}
        onChange={(e) => update('slug', e.target.value)}
        placeholder="pristatymas"
      />
      <label>Pavadinimas * ({langLabel})</label>
      <input
        type="text"
        value={pageMeta[titleKey]}
        onChange={(e) => update(titleKey, e.target.value)}
        placeholder="Pavadinimas"
      />
      <label>Rūšiavimo eilė</label>
      <input
        type="number"
        value={pageMeta.sortOrder}
        onChange={(e) => update('sortOrder', parseInt(e.target.value, 10) || 0)}
      />
      <label>Meta pavadinimas ({langLabel})</label>
      <input
        type="text"
        value={pageMeta[metaTitleKey]}
        onChange={(e) => update(metaTitleKey, e.target.value)}
        placeholder="SEO"
      />
      <label>Meta aprašymas ({langLabel})</label>
      <RichTextEditor
        value={pageMeta[metaDescKey] || ''}
        onChange={(html) => update(metaDescKey, html)}
        placeholder="SEO aprašymas"
        minHeight={60}
      />
      <label className="section-props-checkbox">
        <input
          type="checkbox"
          checked={pageMeta.isActive}
          onChange={(e) => update('isActive', e.target.checked)}
        />
        Aktyvus
      </label>
    </div>
  );
}

export default AdminPageEditor;
